import type { z } from 'zod';
import { type JourneySnapshot, journeySnapshotSchema, SNAPSHOT_TTL_MS } from './schema';
import { type JourneyStorage, localJourneyStorage } from './storage';

/** Entry params can arrive as URLSearchParams (router) or a plain record (tests). */
export type EntryParams = URLSearchParams | Record<string, string | undefined>;

/** Only `sc_*` and `utm_*` keys are kept as attribution; everything else is ignored. */
const TRACKING_KEY = /^(sc_|utm_)/;

function toRecord(params: EntryParams): Record<string, string> {
  // Duck-type on `entries` rather than `instanceof URLSearchParams` so a
  // URLSearchParams from another realm (iframe/window) is still recognised.
  const entries =
    typeof (params as URLSearchParams).entries === 'function'
      ? [...(params as URLSearchParams).entries()]
      : Object.entries(params as Record<string, string | undefined>);
  const out: Record<string, string> = {};
  for (const [key, value] of entries) {
    if (typeof value === 'string' && value !== '') {
      out[key] = value;
    }
  }
  return out;
}

export interface JourneyConfig<T> {
  /** Storage key. One journey type per instance; parallel journeys are separate instances. */
  type: string;
  /** Validates the persisted payload on read, so a schema change ages out stale snapshots. */
  schema: z.ZodType<T>;
  /**
   * Maps normalised entry params to a payload, or null to skip the capture. Empty
   * values are already dropped. Returning null on missing essentials makes a stray
   * re-entry a no-op instead of overwriting a good snapshot.
   */
  parse: (params: Record<string, string>) => T | null;
  /** Time-to-live before a snapshot reads as null. Defaults to two weeks. */
  ttlMs?: number;
  storage?: JourneyStorage;
  now?: () => number;
}

export interface JourneyClient<T> {
  capture(params: EntryParams): JourneySnapshot<T> | null;
  peek(): JourneySnapshot<T> | null;
  read(): JourneySnapshot<T> | null;
  clear(): void;
  /** Notified after every write through this client, and on storage changes it can hear about. */
  subscribe(listener: () => void): () => void;
}

/**
 * Device-local snapshot for one journey type: capture / peek / read / clear.
 * Generic over the payload; `schema`/`parse` inject the journey-specific part.
 * `storage`/`now` are injectable for tests. Latest valid capture wins.
 */
export function createJourneyClient<T>(config: JourneyConfig<T>): JourneyClient<T> {
  const { type, parse, ttlMs = SNAPSHOT_TTL_MS, storage = localJourneyStorage, now = Date.now } = config;
  const envelope = journeySnapshotSchema(config.schema);

  const isExpired = (snapshot: JourneySnapshot<T>) => now() - snapshot.createdAt > ttlMs;

  // Pure: raw string → valid snapshot or null (parse + validate + TTL). No side effects.
  const evaluate = (raw: string | null): JourneySnapshot<T> | null => {
    if (raw === null) {
      return null;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
    const result = envelope.safeParse(parsed);
    if (!result.success || isExpired(result.data as JourneySnapshot<T>)) {
      return null;
    }
    return result.data as JourneySnapshot<T>;
  };

  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) {
      listener();
    }
  };

  // `useSyncExternalStore` compares snapshots by identity, so `peek` must hand back the same
  // object until the stored bytes change.
  let cachedRaw: string | null = null;
  let cachedSnapshot: JourneySnapshot<T> | null = null;

  const peekCached = (): JourneySnapshot<T> | null => {
    const raw = storage.read(type);
    if (raw !== cachedRaw) {
      cachedRaw = raw;
      cachedSnapshot = evaluate(raw);
      // Expiry needs no write, so re-check it on an unchanged entry too.
    } else if (cachedSnapshot !== null && isExpired(cachedSnapshot)) {
      cachedSnapshot = null;
    }
    return cachedSnapshot;
  };

  return {
    /**
     * Seed the snapshot from an entry's params. `parse` returning null means the
     * entry is not a capture (no essential payload), so nothing is written and the
     * existing snapshot stays. Latest valid capture of this type wins.
     */
    capture(params) {
      const record = toRecord(params);
      const payload = parse(record);
      if (payload === null) {
        return null;
      }
      const entryTracking = Object.fromEntries(Object.entries(record).filter(([key]) => TRACKING_KEY.test(key)));
      // Merge attribution rather than overwrite: keep keys from an earlier capture that
      // this entry does not carry, newer values win per key. A product-bearing re-entry
      // must not wipe the original campaign. Expired storage evaluates to null, so stale
      // attribution is not resurrected. The real attribution is the per-capture event the
      // consumer emits; this snapshot only has to stay non-lossy across re-entries.
      const previous = peekCached();
      const snapshot: JourneySnapshot<T> = {
        payload,
        tracking: { ...previous?.tracking, ...entryTracking },
        createdAt: now(),
      };
      if (!envelope.safeParse(snapshot).success) {
        return null;
      }
      const raw = JSON.stringify(snapshot);
      storage.write(type, raw);
      // Primed, so the subscriber woken below does not parse these bytes back into a second object.
      cachedRaw = raw;
      cachedSnapshot = snapshot;
      notify();
      return snapshot;
    },

    /** Valid snapshot, or null. Leaves storage alone, but caches, so it is not a pure function. */
    peek() {
      return peekCached();
    },

    /** Like {@link JourneyClient.peek}, but reaps a stale entry. No notify: it already read as null. */
    read() {
      const snapshot = peekCached();
      if (snapshot === null && cachedRaw !== null) {
        storage.clear(type);
      }
      return snapshot;
    },

    clear() {
      storage.clear(type);
      notify();
    },

    subscribe(listener) {
      listeners.add(listener);
      const unsubscribeStorage = storage.subscribe?.(type, listener);
      return () => {
        listeners.delete(listener);
        unsubscribeStorage?.();
      };
    },
  };
}
